import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-hot-toast';
import { Helmet } from 'react-helmet-async';
import { Eye, EyeOff } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Footer } from '@/components/layout/Footer';
import { useClienteAuthStore } from '@/stores/clienteAuthStore';
import { clienteMobileService } from '@/services/clienteMobileService';
import { useCustomization } from '@/contexts/CustomizationContext';

const LOGO_ZEHMOTOCA = '/logozehmotoca.png';

const MOBILE_COLORS = {
  primary: '#FFC107',
  primaryDark: '#FFA000',
} as const;

const schema = z.object({
  email: z.string().email('E-mail inválido'),
  password: z.string().min(6, 'Senha deve ter pelo menos 6 caracteres'),
});

type Form = z.infer<typeof schema>;

export function ClienteLoginPage() {
  const { customization } = useCustomization();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const isAuthenticated = useClienteAuthStore((s) => s.isAuthenticated);
  const setSession = useClienteAuthStore((s) => s.setSession);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Form>({ resolver: zodResolver(schema) });

  if (isAuthenticated) {
    return <Navigate to="/cliente/dashboard" replace />;
  }

  const onSubmit = async (data: Form) => {
    setLoading(true);
    try {
      const { user, accessToken, refreshToken } = await clienteMobileService.login(data.email, data.password);
      setSession(user, accessToken, refreshToken);
      try {
        const profile = await clienteMobileService.getProfile();
        if (profile.user) {
          useClienteAuthStore.getState().setUser(profile.user);
        }
      } catch {
        /* perfil opcional */
      }
      toast.success('Bem-vindo à área do cliente!');
      navigate('/cliente/dashboard', { replace: true });
    } catch {
      toast.error('E-mail ou senha incorretos. Use as mesmas credenciais do aplicativo Zeh Motoca.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Área do cliente — Login | Zeh Motoca</title>
      </Helmet>
      <div className="min-h-screen flex flex-col bg-[#FAFAFA] dark:bg-[#0a0a0a]">
        <div className="flex flex-1 min-h-0">
          <div
            className="hidden lg:flex lg:w-[50%] flex-col justify-center px-12 py-12 relative overflow-hidden"
            style={{ background: `linear-gradient(to bottom, ${MOBILE_COLORS.primary}, ${MOBILE_COLORS.primaryDark})` }}
          >
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-14 h-14 rounded-xl bg-black/10 flex items-center justify-center overflow-hidden">
                  <img
                    src={customization.logoUrl || LOGO_ZEHMOTOCA}
                    alt="Zeh Motoca"
                    className="w-10 h-10 object-contain"
                  />
                </div>
                <span className="text-black/80 text-sm font-medium">Locatário</span>
              </div>
              <h1 className="text-4xl font-bold text-black tracking-tight mb-4">Área do cliente</h1>
              <p className="text-black/70 text-lg max-w-md leading-relaxed">
                Acompanhe boletos, multas e atendimento com o mesmo e-mail e senha do aplicativo Zeh Motoca.
              </p>
            </div>
          </div>

          <div className="w-full lg:w-[50%] flex items-center justify-center px-6 py-12 bg-white dark:bg-[#0a0a0a]">
            <div className="w-full max-w-md">
              <Card className="border border-border shadow-lg">
                <CardHeader className="text-center pb-2 lg:hidden">
                  <h1 className="text-2xl font-bold">Área do cliente</h1>
                </CardHeader>
                <CardHeader className="pb-4">
                  <div className="flex justify-center mb-4">
                    <div
                      className="w-14 h-14 rounded-xl flex items-center justify-center overflow-hidden p-1"
                      style={{ backgroundColor: MOBILE_COLORS.primary }}
                    >
                      <img src={customization.logoUrl || LOGO_ZEHMOTOCA} alt="" className="w-full h-full object-contain" />
                    </div>
                  </div>
                  <CardTitle className="text-xl font-bold text-center">Entrar</CardTitle>
                  <CardDescription className="text-center">Credenciais do aplicativo (locatário)</CardDescription>
                </CardHeader>
                <CardContent className="pb-8">
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <Input label="E-mail" type="email" placeholder="seu@email.com" error={errors.email?.message} {...register('email')} />
                    <div className="relative">
                      <Input
                        label="Senha"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Sua senha"
                        error={errors.password?.message}
                        {...register('password')}
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-8 text-muted-foreground hover:text-foreground"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <Button
                      type="submit"
                      className="w-full mt-2 !bg-button-brand !text-button-brand-foreground shadow-button hover:shadow-button-hover hover:!bg-button-brand/90 hover:-translate-y-0.5"
                      loading={loading}
                    >
                      Entrar
                    </Button>
                    <p className="text-center text-xs text-muted-foreground">
                      Equipe / cobranças?{' '}
                      <Link to="/login" className="underline hover:text-foreground">
                        Acesso SIC
                      </Link>
                      {' · '}
                      <Link to="/politica-de-privacidade" className="underline hover:text-foreground">
                        Privacidade
                      </Link>
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}
